import { useEffect, useState } from 'react';
import axios from 'axios';
import BaseConocimiento from '../components/BaseConocimiento';

const API = 'https://ticobaterias.com/api';

type Memoria = {
  contenido: string;
  vista_previa: string;
  limite: number;
  actualizado_en: string | null;
  sincronizado_en: string | null;
  sincronizado: boolean;
  error: string | null;
};

type Vehiculo = {
  id: number;
  mensaje: string;
  marca: string | null;
  revisado: boolean;
  creado_en: string;
};

// El backend guarda las fechas en UTC sin zona; se marcan como UTC para mostrarlas en hora local
const fecha = (s: string | null) =>
  s ? new Date(s.endsWith('Z') ? s : s + 'Z').toLocaleString('es-CR') : 'nunca';

export default function AgenteIA() {
  const [memoria, setMemoria] = useState<Memoria | null>(null);
  const [contenido, setContenido] = useState('');
  const [vehiculos, setVehiculos] = useState<Vehiculo[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [verPrevia, setVerPrevia] = useState(false);
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const cargar = () => {
    axios.get(`${API}/memoria-agente/`, { headers })
      .then(r => { setMemoria(r.data); setContenido(r.data.contenido || ''); })
      .catch(() => {});
    axios.get(`${API}/memoria-agente/vehiculos`, { headers })
      .then(r => setVehiculos(r.data))
      .catch(() => {});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const mostrarAviso = (tipo: 'ok' | 'error', texto: string) => {
    setAviso({ tipo, texto });
    setTimeout(() => setAviso(null), 6000);
  };

  const resultado = (data: Memoria) => {
    setMemoria(data);
    if (data.sincronizado) mostrarAviso('ok', '✅ Guardado y sincronizado con el agente');
    else mostrarAviso('error', `⚠️ Guardado, pero no se pudo sincronizar: ${data.error}`);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await axios.put(`${API}/memoria-agente/`, { contenido }, { headers });
      resultado(r.data);
    } catch (e: any) {
      mostrarAviso('error', e?.response?.data?.detail || '❌ No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const resincronizar = async () => {
    setGuardando(true);
    try {
      const r = await axios.post(`${API}/memoria-agente/sincronizar`, {}, { headers });
      resultado(r.data);
    } catch (e: any) {
      mostrarAviso('error', e?.response?.data?.detail || '❌ No se pudo sincronizar');
    } finally {
      setGuardando(false);
    }
  };

  const marcarRevisado = async (id: number) => {
    try {
      await axios.put(`${API}/memoria-agente/vehiculos/${id}/revisado`, {}, { headers });
      setVehiculos(v => v.filter(x => x.id !== id));
    } catch {
      mostrarAviso('error', '❌ No se pudo marcar como revisado');
    }
  };

  const limite = memoria?.limite ?? 3000;
  const excedido = contenido.length > limite;
  const sinGuardar = memoria !== null && contenido !== (memoria.contenido || '');

  return (
    <div>
      <div className="flex justify-between items-center mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-bold text-2xl text-azul">Agente IA</h1>
          <p className="text-gray-500 text-sm">Lo que sabe el asistente del chat del sitio</p>
        </div>
        {aviso && (
          <div className={`px-4 py-2 rounded-xl text-sm font-bold ${aviso.tipo === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {aviso.texto}
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* ---------- Memoria ---------- */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="font-bold text-azul mb-3">🧠 Información adicional para el agente</h2>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-gray-600 mb-4 space-y-1">
            <p>🔋 <b>Precios y baterías:</b> se toman automáticamente del catálogo. No hace falta escribirlos aquí.</p>
            <p>📍 <b>Teléfono, dirección, horario y redes:</b> se toman de la sección <b>Información</b>.</p>
            <p>✍️ <b>Aquí escribe lo demás:</b> garantía, si la instalación va incluida, formas de pago, servicio a domicilio, etc.</p>
          </div>

          <textarea
            value={contenido}
            onChange={e => setContenido(e.target.value)}
            rows={10}
            placeholder={'Ejemplos:\n- Todas las baterías incluyen instalación gratis en el local.\n- Garantía de 12 meses en baterías LTH y Gonher.\n- Aceptamos SINPE Móvil, tarjeta y efectivo.\n- Servicio a domicilio en el Gran Área Metropolitana con costo adicional.'}
            className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none resize-y ${excedido ? 'border-red-400' : 'border-gray-200 focus:border-azul'}`}
          />

          <div className="flex justify-between items-center text-xs mt-1 mb-4 flex-wrap gap-2">
            <span className="text-gray-400">
              Última sincronización con el agente: {fecha(memoria?.sincronizado_en ?? null)}
              {sinGuardar && <span className="text-orange-500 font-bold"> · Hay cambios sin guardar</span>}
            </span>
            <span className={excedido ? 'text-red-500 font-bold' : 'text-gray-400'}>
              {contenido.length} / {limite}
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <button
              onClick={guardar}
              disabled={guardando || excedido}
              className="md:col-span-2 bg-azul text-white py-3 rounded-xl font-bold hover:bg-azul-dark transition-colors disabled:opacity-50"
            >
              {guardando ? '⏳ Guardando...' : '💾 Guardar y sincronizar'}
            </button>
            <button
              onClick={resincronizar}
              disabled={guardando}
              title="Úsalo después de cambiar datos en Información"
              className="border border-azul text-azul py-3 rounded-xl font-bold hover:bg-blue-50 transition-colors disabled:opacity-50"
            >
              🔄 Re-sincronizar
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Si cambias el horario, la dirección u otros datos en <b>Información</b>, presiona <b>Re-sincronizar</b> para que el agente los conozca.
          </p>
        </div>

        {/* ---------- Vista previa ---------- */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <button onClick={() => setVerPrevia(!verPrevia)} className="w-full flex justify-between items-center">
            <h2 className="font-bold text-azul">👁️ Lo que recibe el agente</h2>
            <span className="text-gray-400 text-sm">{verPrevia ? 'Ocultar ▲' : 'Ver ▼'}</span>
          </button>
          {verPrevia && (
            <pre className="mt-4 bg-gray-50 rounded-xl p-4 text-xs text-gray-700 whitespace-pre-wrap font-sans">
              {memoria?.vista_previa || 'Cargando...'}
            </pre>
          )}
        </div>

        {/* ---------- Base de conocimiento (catalogos) ---------- */}
        <BaseConocimiento />

        {/* ---------- Vehiculos sin compatibilidad ---------- */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h2 className="font-bold text-azul mb-1">🚗 Vehículos sin compatibilidad ({vehiculos.length})</h2>
          <p className="text-gray-500 text-sm mb-4">
            Clientes preguntaron por estos vehículos y no están en la tabla de compatibilidad. Agrega la batería correcta
            y márcalo como revisado: desde ese momento el agente lo podrá cotizar.
          </p>

          {vehiculos.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-6">✅ No hay vehículos pendientes</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {vehiculos.map(v => (
                <div key={v.id} className="py-3 flex justify-between items-start gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800 break-words">"{v.mensaje}"</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {v.marca ? `Marca detectada: ${v.marca}` : 'Marca no reconocida'} · {fecha(v.creado_en)}
                    </p>
                  </div>
                  <button
                    onClick={() => marcarRevisado(v.id)}
                    className="shrink-0 text-xs border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-colors"
                  >
                    ✓ Revisado
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
